import { createContext, useContext, useMemo, type CSSProperties, type ReactNode } from 'react';

import { useCloudSynced } from '@/hooks';

export type TextSize = 'small' | 'std' | 'large';

export const TEXT_SIZES: TextSize[] = ['small', 'std', 'large'];

export const ZOOM: Record<TextSize, number> = { small: 0.85, std: 1, large: 1.15 };

export const ZOOMED = '[zoom:var(--zoom,1)]';

const KEY = 'ui_zoom';

const RETIRED = new Map<unknown, TextSize>([
  ['compact', 'small'],
  ['xl', 'large'],
]);

export const asSize = (raw: unknown): TextSize | null =>
  TEXT_SIZES.find((s) => s === raw) ?? RETIRED.get(raw) ?? null;

interface TextSizeState {
  size: TextSize;
  setSize: (size: TextSize) => void;
}

const Ctx = createContext<TextSizeState>({ size: 'std', setSize: () => {} });

export function TextSizeProvider({ children }: { children: ReactNode }) {
  const [size, setSize] = useCloudSynced<TextSize>(KEY, asSize, 'std');
  const value = useMemo(() => ({ size, setSize }), [size, setSize]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTextSize(): TextSizeState {
  return useContext(Ctx);
}

export function useZoom(): { 'data-text-size': TextSize; style: CSSProperties } {
  const { size } = useTextSize();
  return { 'data-text-size': size, style: { '--zoom': ZOOM[size] } as CSSProperties };
}

export function TextSizeFrame({ children }: { children: ReactNode }) {
  return (
    <div {...useZoom()} className={ZOOMED}>
      {children}
    </div>
  );
}
